import { CalendarOff, Pencil, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { HolidayRule } from "@/lib/tickets";

/**
 * How a rule reads in words — "4th Thursday in November", "25 December".
 *
 * ⚠️ Shown ALONGSIDE the resolved dates, never instead of them. The words are what an
 * administrator edits; the dates are how they check the words say what they meant.
 */
export function describeRule(holiday: HolidayRule): string {
  const month = MONTHS[holiday.month - 1] ?? `month ${holiday.month}`;

  if (holiday.kind === "FixedDate") {
    const shifted = holiday.observedOnNearestWeekday ? ", moved off weekends" : "";
    return `${holiday.day} ${month}${describeOffset(holiday.offsetDays)}${shifted}`;
  }

  const ordinal =
    holiday.ordinal === -1
      ? "last"
      : (ORDINALS[(holiday.ordinal ?? 1) - 1] ?? `${holiday.ordinal}th`);

  return `${ordinal} ${holiday.weekday} in ${month}${describeOffset(holiday.offsetDays)}`;
}

/**
 * ⚠️ The offset is SAID, not left for the reader to infer from the dates. "4th Thursday in
 * November" reads as Thanksgiving; the row that means the day after has to say so, or an
 * administrator checking the list sees two rules that look identical.
 */
function describeOffset(offsetDays: number): string {
  if (offsetDays === 0) return "";
  if (offsetDays === 1) return ", plus a day";
  if (offsetDays === -1) return ", less a day";

  return offsetDays > 0 ? `, plus ${offsetDays} days` : `, less ${Math.abs(offsetDays)} days`;
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ORDINALS = ["1st", "2nd", "3rd", "4th", "5th"];

/**
 * The days a desk is closed, with what each rule actually resolves to.
 *
 * ⚠️ THE DATES ARE THE POINT OF THE LIST. A rule is the durable thing — it has no last year in
 * it, unlike a table of dates somebody retypes each December — but a rule is also something you
 * can get subtly wrong, and "4th Thursday in November" off by one reads perfectly and closes the
 * wrong day. Printing the next three occurrences is what makes that visible.
 *
 * ⚠️ A rule that resolves to NO dates is called out rather than left looking normal. It is a row
 * that appears in the list as a holiday and silently never closes anything.
 */
export function HolidayList({
  holidays,
  onToggle,
  onEdit,
  onRemove,
  disabled,
}: {
  holidays: HolidayRule[];
  onToggle: (holiday: HolidayRule, isObserved: boolean) => void;
  onEdit?: (holiday: HolidayRule) => void;
  onRemove?: (holiday: HolidayRule) => void;
  disabled?: boolean;
}) {
  if (holidays.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No holidays. Business-hours targets advance on every open day.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-border">
      {holidays.map((h) => (
        <li key={h.id} className="flex flex-wrap items-start gap-x-3 gap-y-1 py-2">
          <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2">
            <input
              type="checkbox"
              checked={h.isObserved}
              disabled={disabled}
              onChange={(e) => onToggle(h, e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0"
            />
            <span className="min-w-0">
              <span
                className={cn(
                  "block truncate text-sm",
                  !h.isObserved && "text-muted-foreground line-through",
                )}
              >
                {h.name}
              </span>
              <span className="mt-0.5 block text-xs text-muted-foreground">{describeRule(h)}</span>
            </span>
          </label>

          <div className="flex shrink-0 items-center gap-2">
            {h.nextDates.length === 0 ? (
              <span className="inline-flex items-center gap-1 text-xs text-warning">
                <CalendarOff className="h-3.5 w-3.5" aria-hidden />
                falls on no date
              </span>
            ) : (
              <span
                className={cn(
                  "text-xs tabular-nums text-muted-foreground",
                  !h.isObserved && "opacity-50",
                )}
              >
                {h.nextDates.join(" · ")}
              </span>
            )}

            {onEdit && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => onEdit(h)}
                aria-label={`Edit ${h.name}`}
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-input px-2 text-xs transition hover:border-ring disabled:opacity-50"
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden />
                Edit
              </button>
            )}

            {onRemove && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => onRemove(h)}
                aria-label={`Remove ${h.name}`}
                className="inline-flex h-8 items-center rounded-lg border border-input px-2 text-xs text-muted-foreground transition hover:border-destructive/50 hover:text-destructive disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
